<template>
  <div>
    <div class="flex">
      <div class="mr-auto leading-loose">
        <div class="text-lg font-bold">{{ header }}</div>
        <div class="text-lg text-top">{{ price | toCurrency }}</div>
      </div>
      <div>
        <template>
          <button
            v-if="!calendarOpen"
            @click="calendarOpen = true"
            class="w-40 py-2 px-5 bg-top text-top-text rounded-sm border-0 font-bold shadow-none hover:shadow-sm hover:cursor-pointer transition-all duration-3s"
          >Vælg dato</button>
          <button
            v-else
            @click="calendarOpen = false"
            class="w-40 py-2 px-5 border outline-none border-gray-300 rounded-sm text-center"
          >Luk</button>
        </template>
      </div>
    </div>
    <div class="mt-4">
      <slot></slot>
    </div>

    <div class="mt-4 border-gray-300 relative" v-if="calendarOpen">
      <spinner v-if="loading" class="absolute" style="top:50%;left:50%;translate(-50%,-50%)"></spinner>
      <div v-if="calendarIsFine" :class="{'opacity-25': loading}">
        <div class="border border-gray-300 flex p-2 items-center">
          <button
            class="p-2 bg-transparent leading-none"
            @click="goBack()"
            :disabled="!canGoBack"
          >
            <svg class="fill-current h-4 w-3" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.845 8l6.078 6.077-1.179 1.179L.488 8 7.744.744l1.179 1.179L2.845 8z" />
            </svg>
          </button>
          <button
            class="p-2 bg-transparent leading-none"
            @click="goForward()"
            :disabled="!canGoForward"
          >
            <svg class="fill-current h-4 w-3" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.155 8L.077 1.923 1.256.744 8.512 8l-7.256 7.256-1.179-1.179L6.155 8z" />
            </svg>
          </button>
          <div
            class="p-2 text-base leading-none font-bold"
            v-if="mode == 'week'"
          >Uge {{ calendar.Week }}</div>
          <div class="p-2 text-base leading-none" v-if="mode == 'day'">
            <span class="font-bold">{{ currentDay.Day }}</span>
            - {{ currentDay.Date }}
          </div>
          <div
            class="ml-auto rounded-sm border border-gray-300 flex"
            v-if="!calendar.IsSingleResource && !calendar.ShouldBeDayMode"
          >
            <button
              class="flex justify-center px-3"
              :class="{'bg-gray-50 font-bold': mode == 'day', 'bg-white text-gray-400': mode != 'day'}"
              @click="mode = 'day'"
            >Dag</button>
            <button
              class="flex justify-center px-3"
              :class="{'bg-gray-50 font-bold': mode == 'week', 'bg-white text-gray-400': mode != 'week'}"
              @click="mode = 'week'"
            >Uge</button>
          </div>
        </div>

        <div class="border border-gray-300 border-t-0 p-4">
          <div class="inline-flex mr-4 items-center">
            <div class="w-4 h-4 bg-green-500"></div>
            <div class="ml-2">Ledig</div>
          </div>
          <div class="inline-flex mr-4 items-center">
            <div class="w-4 h-4 bg-orange-500"></div>
            <div class="ml-2">Venteliste</div>
          </div>
          <div class="inline-flex mr-4 items-center">
            <div class="w-4 h-4 bg-gray-100"></div>
            <div class="ml-2">Udsolgt</div>
          </div>
          <div class="inline-flex mr-4 items-center">
            <div class="w-4 h-4 bg-blue-500"></div>
            <div class="ml-2">I indkøbskurven</div>
          </div>
        </div>

        <div class="border border-gray-300 border-t-0 flex flex-col">
          <div v-if="mode == 'day'" class="flex flex-col">
            <div class="p-2">Varighed: {{ duration }}</div>

            <div
              v-for="resource in currentDay.Resources"
              :key="resource.Key"
              class="flex flex-col sm:flex-row py-2 ml-2 border-b border-gray-300"
            >
              <div class="sm:w-1/5 font-bold flex-shrink-0">{{ resource.Name }}</div>
              <div class="flex flex-wrap" style="margin-left:-.25rem;margin-right:-.25rem;">
								<template v-for="slot in resource.Timeslots">
									<button
                  	@click="addToBasket(slot)"
										class="m-1 border border-current bg-white p-1 min-w-19 inline-flex items-baseline text-left"
										:class="{'text-gray-400': slot.SoldOut && !waitingListAllowed && !isInBasket(slot), 'text-orange-500': slot.SoldOut && waitingListAllowed && !isInBasket(slot), 'text-blue-500': isInBasket(slot), 'text-green-500': !isInBasket(slot) && !slot.SoldOut }"
									>
										<span class="font-bold">{{ slot.Time }}</span>
											<small class="pl-1">{{ slot.AvailableSeats }} stk.</small>
									</button>
									<add-to-waiting-list-button :visible="addToWaitingList == slot.ProductId" :productId="slot.ProductId" ng-if="waitingListAllowed" @close="addToWaitingList = null"></add-to-waiting-list-button>
								</template>
              </div>
            </div>
          </div>
          <div v-else-if="mode == 'week' && !calendar.IsSingleResource">
            <div v-for="day in calendar.Days" :key="day.Date" class="flex flex-col p-2">
              <div class="flex flex-wrap">
                <div class="pr-2 mr-auto">
                  <span class="font-bold">{{ day.Day }}</span>
                  - {{ day.Date }}
                </div>
                <div>Varighed: {{ duration }}</div>
              </div>
              <div
                v-for="resource in day.Resources"
                :key="resource.Key"
                class="flex flex-col sm:flex-row m-2 pb-2 border-b border-gray-300"
              >
                <div class="sm:w-1/5 sm:py-2 font-bold flex-shrink-0">{{ resource.Name }}</div>
                <div class="flex flex-wrap" style="margin-left:-.25rem;margin-right:-.25rem;">
									<template v-for="slot in resource.Timeslots">
										<button
											@click="addToBasket(slot)"
											class="m-1 border border-current bg-white p-1 min-w-19 inline-flex items-baseline text-left"
											:class="{'text-gray-400 cursor-default': slot.SoldOut && !waitingListAllowed && !isInBasket(slot), 'text-orange-500': slot.SoldOut && waitingListAllowed && !isInBasket(slot), 'text-blue-500 cursor-default': isInBasket(slot), 'text-green-500': !isInBasket(slot) && !slot.SoldOut }"
										>
											<span class="font-bold">{{ slot.Time }}</span>
											<small class="pl-1">{{ slot.AvailableSeats }} stk.</small>
										</button>
										<add-to-waiting-list-button :visible="addToWaitingList == slot.ProductId" :productId="slot.ProductId" ng-if="waitingListAllowed" @close="addToWaitingList = null"></add-to-waiting-list-button>
									</template>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div v-for="day in calendar.Days" :key="day.Date" class="flex flex-col pt-2">
              <div class="flex flex-wrap p-2">
                <div class="pr-2 mr-auto">
                  <span class="font-bold">{{ day.Day }}</span>
                  - {{ day.Date }}
                </div>
                <div>Varighed: {{ duration }}</div>
              </div>
              <div
                v-for="resource in day.Resources"
                :key="resource.Key"
                class="flex flex-col sm:flex-row py-2 border-b border-gray-300"
              >
                <div class="flex flex-wrap" style="margin-left:-.25rem;margin-right:-.25rem;">
									<template	v-for="slot in resource.Timeslots">
										<button
											@click="addToBasket(slot)"
											class="m-1 border border-current bg-white p-1 min-w-19 inline-flex items-baseline text-left"
											:class="{'text-gray-400 cursor-default': slot.SoldOut && !waitingListAllowed && !isInBasket(slot), 'text-orange-500': slot.SoldOut && waitingListAllowed && !isInBasket(slot), 'text-blue-500 cursor-default': isInBasket(slot), 'text-green-500': !isInBasket(slot) && !slot.SoldOut }"
										>
											<span class="font-bold">{{ slot.Time }}</span>
											<small class="pl-1">{{ slot.AvailableSeats }} stk.</small>
										</button>
										<add-to-waiting-list-button :visible="addToWaitingList == slot.ProductId" :productId="slot.ProductId" ng-if="waitingListAllowed" @close="addToWaitingList = null"></add-to-waiting-list-button>
									</template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import store from "./store";
import axios from "axios";
import AddToWaitingListButton from "./AddToWaitingListButton.vue";

import { bus } from '../index';

export default {
  components: {
		AddToWaitingListButton
	},
  data() {
    return {
      calendarOpen: false,
      toAdd: 0,
      loading: false,
      loaded: false,
      currentDayIndex: 0,
			mode: "week",
			addToWaitingList: "",
      calendar: {}
    };
  },
  created() {
    bus.$on("basketUpdated", (data) => {
			if (this.calendar.Week && this.calendar.Year) {
				this.getWeek(
					this.calendar.Week,
					this.calendar.Year,
					this.currentDayIndex
				);
			}
    });
  },
  computed: {
    duration: function() {
      if (this.calendar.Days) {
        var timeslots = this.calendar.Days.map(d => {
          var ts = d.Resources.filter(r => r.Timeslots.length > 0).map(r => r.Timeslots[0]);
          if (ts.length > 0) {
            return ts[0].Duration;
          }
        });
        if (timeslots.length > 0) {
          return timeslots[0];
        }
      }
      return "";
    },
    currentDay: function() {
      if (this.calendar && this.calendar.Days.length >= this.currentDayIndex) {
        return this.calendar.Days[this.currentDayIndex];
      }
      return null;
    },
    canGoForward: function() {
      if (
        this.mode == "day" &&
        this.currentDayIndex < this.calendar.Days.length
      ) {
        return true;
      }
      return this.calendar.CanGoForward;
    },
    canGoBack: function() {
      if (this.mode == "day" && this.currentDayIndex > 0) {
        return true;
      }
      return this.calendar.CanGoBack;
    },
    calendarIsFine: function() {
      return this.calendar && this.calendar.Days && this.currentDay;
    }
  },
  props: {
    productId: String,
    header: String,
    price: Number,
    waitingListAllowed: Boolean
  },
  mounted() {
    this.getWeek(0, 0, 0, false);
  },
  methods: {
		isInBasket: function(slot) {
			if (slot.ProductId == "00000000-0000-0000-0000-000000000000") return false;
			return this.quantityInBasket(slot.ProductId) > 0;
		},
    quantityInBasket: function(productId) {
      return this.$store.getters.quantityInBasket(productId);
    },
    addToBasket: function(timeslot) {
			if (this.isInBasket(timeslot)) {

			}
			else {
				if (!timeslot.SoldOut) {
					this.$store
						.dispatch("addTimeslotToBasket", {
							parentProductId: this.productId,
							productId: timeslot.ProductId,
							productName: timeslot.ProductName,
							resource: timeslot.Resource,
							startDate: timeslot.StartDateString,
							endDate: timeslot.EndDateString
						})
						.then(() => {
							if (timeslot.ProductId == "00000000-0000-0000-0000-000000000000") {
								this.getWeek(
									this.calendar.Week,
									this.calendar.Year,
									this.currentDayIndex
								);
							}
            })
            .catch(response => {

                if (response.noSuccess) {
                    this.$store.dispatch("updateBasket").then(r => {
                        setTimeout(() => {
                        if (this.isInBasket(timeslot) == false) {
                            alert(response.message)
                            this.isSoldOut = true;
                        }
                        }, 100);
                    })
                }
            });
				} else if (this.waitingListAllowed) {
					this.addToWaitingList = timeslot.ProductId;
					console.log("add to waitinglist");
				}
			}
    },
    goForward: function() {
      if (this.mode == "day") {
        if (this.currentDayIndex + 1 == this.calendar.Days.length) {
          this.getWeek(this.calendar.Week + 1, this.calendar.Year);
        } else {
          this.currentDayIndex++;
        }
      } else {
        this.getWeek(this.calendar.Week + 1, this.calendar.Year);
      }
    },
    goBack: function() {
      if (this.mode == "day") {
        if (this.currentDayIndex == 0) {
          this.getWeek(this.calendar.Week - 1, this.calendar.Year, 6, true);
        } else {
          this.currentDayIndex--;
        }
      } else {
        this.getWeek(this.calendar.Week - 1, this.calendar.Year, 0, true);
      }
    },
    getWeek: function(week, year, setCurrentDayIndex, goBack) {
      this.loading = true;
      axios
        .get(
          "/umbraco/Checkout/Basket/GetBookingTimeslots?productId=" +
            this.productId +
            "&week=" +
            week +
            "&year=" +
            year +
            "&directionBack=" +
            (goBack ? "True" : "False")
        )
        .then(response => {
          if (response.data.Data.Week > 0) {
            this.calendar = response.data.Data;
            this.currentDayIndex = setCurrentDayIndex ? Math.min(setCurrentDayIndex, this.calendar.Days.length-1) : 0;

            if (this.calendar.ShouldBeDayMode) {
              this.mode = "day";
            }
          }
          else {
            if (goBack) {
              this.calendar.CanGoBack = false;
            }
          }
          this.loading = false;
        });
    }
  }
};
</script>
